import { useEffect, useRef, useState } from "react";

/* Same polling rhythm and stage detection as AovAuditExperience (the status
   endpoint doubles as the stuck-build watchdog); the kitchen does the
   talking here. */
const STAGES = [
  { key: "reading", title: "Taking your order", detail: (s) => (s.products_count > 0 ? `${s.products_count.toLocaleString()} products read and counting…` : "Connecting to your storefront…") },
  { key: "basket", title: "Prepping the ingredients", detail: () => "Finding what sells together, picking hero products…" },
  { key: "scoring", title: "In the oven", detail: () => "Scoring your store against the ready ones…" },
  { key: "ready", title: "Order up", detail: () => "Taking you to your menu now…" },
];

function stageIndex(status) {
  if (status.audit_ready) return 3;
  if (status.audit_status === "auditing") return 2;
  if (status.shop_status === "analyzing" || status.shop_status === "generating") return 1;
  return 0;
}

export default function PizzaMenuLoading({ statusPath }) {
  const [status, setStatus] = useState({ audit_status: "pending", shop_status: "pending", products_count: 0 });
  const [failed, setFailed] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const response = await fetch(statusPath, { headers: { Accept: "application/json" } });
        if (!response.ok) throw new Error(`status ${response.status}`);
        const data = await response.json();
        if (cancelled) return;

        setStatus(data);
        if (data.audit_status === "failed") {
          setFailed(data.error_message || "Something went wrong while auditing this store.");
          return;
        }
        if (data.audit_ready) {
          setTimeout(() => { window.location.href = data.audit_path; }, 1200);
          return;
        }
        timer.current = setTimeout(poll, 1500);
      } catch {
        if (!cancelled) timer.current = setTimeout(poll, 3000);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [statusPath]);

  if (failed) {
    return (
      <div className="snag-card">
        <h3>We hit a snag in the kitchen</h3>
        <p>{failed}</p>
        <button className="btn tomato" onClick={() => window.location.reload()}>Try again</button>
      </div>
    );
  }

  const activeIndex = stageIndex(status);

  return (
    <div aria-live="polite">
      <div className="oven-stage" aria-hidden="true">🍕</div>
      <ol className="order-steps">
        {STAGES.map((stage, index) => {
          const state = index < activeIndex ? "done" : index === activeIndex ? "active" : "todo";
          return (
            <li key={stage.key} className={state}>
              <span className="dot">{state === "done" ? "✓" : index + 1}</span>
              <span>
                <span className="step-title">{stage.title}</span>
                {state === "active" && <span className="step-detail">{stage.detail(status)}</span>}
              </span>
            </li>
          );
        })}
      </ol>
      <p className="oven-fine">This usually takes a minute or two. Your menu will be waiting on this page.</p>
    </div>
  );
}
